<script lang="ts">
  /**
   * L'écran Perso : ce que le personnage **est**.
   *
   * Rien de ce qui s'affiche ici n'a été enregistré, sauf les Caractéristiques
   * brutes : les dérivées, les Valeurs réelles et les plafonds se recalculent à
   * l'ouverture.
   *
   * Cet écran ne fait plus qu'assembler. Les quatre panneaux qui vivent leur
   * propre vie — la Prouesse, le garage, le Rang, la dépense d'XP — sont des
   * composants à part : chacun lit ce dont il a besoin auprès du moteur, et ne
   * reçoit d'ici que les gestes qui écrivent sur la fiche.
   */
  import NombreSaisi from '../../composants/NombreSaisi.svelte'
  import Alchimie from './perso/Alchimie.svelte'
  import Compagnon from './perso/Compagnon.svelte'
  import Depense from './perso/Depense.svelte'
  import Machines from './perso/Machines.svelte'
  import Magie from './perso/Magie.svelte'
  import MainsDeDieu from './perso/MainsDeDieu.svelte'
  import Prouesse from './perso/Prouesse.svelte'
  import Rangs from './perso/Rangs.svelte'
  import { domaines as lireLesDomaines } from '../../rules'
  import type {
    Compteur,
    Fiche,
    IssueDeSort,
    MoyenDeRachat,
    PersonnageEnregistre,
  } from '../../rules'

  let {
    vue,
    personnage,
    ajuster,
    acheter,
    acheterMaitrise,
    acheterCapacite,
    promouvoir,
    basculerNarratif,
    basculerGreffe,
    accomplirLeSacrement,
    garage,
    baptiser,
    abimer,
    boulonner,
    forger,
    acheterMain,
    dessiner,
    baptiserLaMain,
    chargerLaMain,
    defaireLaMain,
    apprivoiser,
    instruire,
    ecrireUnRituel,
    effacerUnRituel,
    designerUnFavori,
    choisirUnElement,
    jouerUneCarte,
    racheterUneCarte,
    nouvelleJournee,
    part,
  }: {
    vue: Fiche
    personnage: PersonnageEnregistre
    ajuster: (cible: Compteur, delta: number) => void
    /** Achète une Augmentation : la valeur monte, l'XP dépensée suit. */
    acheter: (quoi: 'caracteristique' | 'competence', id: string) => void
    /** Acquiert ou améliore une Maîtrise. */
    acheterMaitrise: (categorie: string) => void
    /** Apprend une Capacité commune, Prérequis vérifiés. */
    acheterCapacite: (id: string) => void
    /** Franchit un Rang, une fois tout réuni. */
    promouvoir: (noeud: string) => void
    /** Coche, ou décoche, un Prérequis que la table a tranché. */
    basculerNarratif: (texte: string) => void
    /** Greffe, ou retire, une pièce de Prouesse. */
    basculerGreffe: (greffe: string) => void
    /** Accomplit une cérémonie de Rang, ou revient dessus. */
    accomplirLeSacrement: (sacrement: string) => void
    /** Met une machine au garage, ou l'en retire. */
    garage: (modele: string | null, index: number | null) => void
    /** Baptise une machine. */
    baptiser: (index: number, nom: string) => void
    /** Encaisse des Dégâts sur une machine, ou en répare. */
    abimer: (index: number, subis: number) => void
    /** Boulonne un équipement sur une machine, ou le démonte. */
    boulonner: (index: number, equipement: string) => void
    /** Forge une Main de Dieu du livre, sur son Plan — le Test dit si elle arrive. */
    forger: (modele: string, reussi: boolean) => void
    /** Achète une Main toute faite, au prix du livre en Points d'Influence. */
    acheterMain: (modele: string) => void
    /** Inscrit une Main de Dieu que la table a dessinée. */
    dessiner: (main: {
      nom: string
      niveau: number
      competence: string
      degats: number | null
      plusForceDeFrappe: boolean
      portee: string | null
      special: string | null
      tribut: string | null
    }, reussi: boolean) => void
    /** Baptise une Main de Dieu. */
    baptiserLaMain: (index: number, nom: string) => void
    /** Charge une Main de Dieu, ou en dépense une munition. */
    chargerLaMain: (index: number, delta: number) => void
    /** Se défait d'une Main de Dieu. */
    defaireLaMain: (index: number) => void
    /** Apprivoise un animal, ou renvoie celui qu'on avait. */
    apprivoiser: (profil: string | null) => void
    /** Ce que le Compagnon subit, apprend, ou devient. */
    instruire: (
      quoi: 'nom' | 'pv' | 'affinite' | 'ordre' | 'reflexe',
      texte: string,
      valeur: number,
    ) => void
    /** Inscrit un Rituel que le meneur a écrit. */
    ecrireUnRituel: (nom: string, cercle: string, duree: string | null, effet: string) => void
    /** Efface un Rituel de la table. */
    effacerUnRituel: (index: number) => void
    /** Désigne un paquet favori du Cartomancien — celui qui porte un Joker. */
    designerUnFavori: (sphere: string) => void
    /** Choisit un élément d'Union, ou y renonce. */
    choisirUnElement: (element: string) => void
    /** Enregistre une carte tirée, et ce qu'elle est devenue. */
    jouerUneCarte: (
      sphere: string,
      carte: string,
      issue: IssueDeSort,
      horsLimite: boolean,
    ) => void
    /** Reprend une carte de la Défausse. */
    racheterUneCarte: (sphere: string, carte: string, moyen: MoyenDeRachat) => void
    /** Une nouvelle journée en jeu : la limite de pioche repart de zéro. */
    nouvelleJournee: () => void
    part: (valeur: number, total: number) => number
  } = $props()

  const c = $derived(vue.compteurs)

  /**
   * Ce que la Classe du personnage lui donne le droit de voir.
   *
   * Le livre range des mécaniques entières dans le Codex d'une Classe : le
   * garage au Technodiacre, le Compagnon au Rôdeur, les Cercles à l'Alchimiste,
   * les cartes au Cartomancien. Tout montrer à tout le monde noyait la fiche
   * sous des panneaux qui ne s'ouvriraient jamais, et laissait croire à un
   * Technodiacre qu'il pouvait tirer les cartes.
   *
   * Le moteur tranche, pas cet écran : la règle est dans le domaine, avec ses
   * exceptions — la Prouesse qui n'est fermée à personne, les Mains de Dieu que
   * le Soldat de la Couronne forge aussi, et le panneau qui se rouvre dès que
   * la fiche y porte quelque chose.
   */
  const ouvert = $derived(lireLesDomaines(personnage))

  /** Ouvert à la demande : la liste des 41 Compétences n'a rien à faire là en
      permanence. */
  let depense = $state(false)
</script>

<div class="cartouche">
  <div class="bandeau">Caractéristiques</div>
  <div class="corps">
    <div class="caracs">
      {#each vue.caracteristiques as carac (carac.id)}
        <div class="chiffre" title={carac.nom}>
          <span class="etiquette">{carac.code}</span>
          <span class="valeur data">{carac.valeur}</span>
        </div>
      {/each}
    </div>
  </div>
</div>

<div class="cartouche">
  <div class="bandeau">Caractéristiques dérivées</div>
  <div class="corps">
    <p class="chapo">
      Aucune n'est enregistrée : les huit se recalculent depuis les Valeurs
      réelles, à chaque ouverture de la fiche.
    </p>
    <div class="caracs">
      {#each vue.derivees as d (d.id)}
        <div class="chiffre" title={d.nom}>
          <span class="etiquette">{d.abrev}</span>
          <span class="valeur data">{d.valeur}</span>
        </div>
      {/each}
    </div>
  </div>
</div>

<div class="cartouche">
  <div class="bandeau">Capacités</div>
  <div class="corps">
    <ul class="capacites">
      {#each vue.capacites as cap (cap.id)}
        <li>
          <strong>
            {cap.nom}{#if cap.parametre} ({cap.parametre}){/if}
          </strong>
          <span class="voie">{cap.source === 'classe' ? 'de Classe' : 'commune'}</span>
          <span class="resume">{cap.resume}</span>
        </li>
      {/each}
    </ul>
  </div>
</div>

{#if ouvert.prouesse}
  <Prouesse {personnage} {basculerGreffe} />
{/if}

{#if ouvert.machines}
  <Machines {personnage} {garage} {baptiser} {abimer} {boulonner} />
{/if}

{#if ouvert.mains_de_dieu}
  <MainsDeDieu
    {vue}
    {personnage}
    {forger}
    acheter={acheterMain}
    {dessiner}
    baptiser={baptiserLaMain}
    charger={chargerLaMain}
    defaire={defaireLaMain}
  />
{/if}

{#if ouvert.compagnon}
  <Compagnon {personnage} {apprivoiser} {instruire} />
{/if}

{#if ouvert.alchimie}
  <Alchimie {personnage} ecrire={ecrireUnRituel} effacer={effacerUnRituel} />
{/if}

{#if ouvert.magie}
  <Magie
    {personnage}
    favori={designerUnFavori}
    element={choisirUnElement}
    jouer={jouerUneCarte}
    racheter={racheterUneCarte}
    journee={nouvelleJournee}
  />
{/if}

<Rangs {personnage} {promouvoir} {basculerNarratif} {accomplirLeSacrement} />

{#if vue.maitrises.length > 0}
  <div class="cartouche">
    <div class="bandeau">Maîtrises</div>
    <div class="corps">
      <ul class="plates">
        {#each vue.maitrises as m (m.categorie)}
          <li>{m.nom} <span class="niveau">Niveau {m.niveau}</span></li>
        {/each}
      </ul>
    </div>
  </div>
{/if}

{#if depense}
  <Depense
    {vue}
    {personnage}
    {acheter}
    {acheterMaitrise}
    {acheterCapacite}
    {basculerNarratif}
    fermer={() => (depense = false)}
  />
{/if}

<div class="cartouche progression">
  <div class="bandeau">Progression</div>
  <div class="corps duo">
    <div class="jauge">
      <div class="entete">
        <span class="titre">Expérience</span>
        <span class="compte">
          <NombreSaisi
            valeur={c.xp_disponible}
            min={0}
            max={c.xp_total}
            libelle="XP disponibles"
            pas={10}
            taille="grand"
            onchange={(v) => ajuster('experience_depensee', c.xp_disponible - v)}
          />
          /
          <NombreSaisi
            valeur={c.xp_total}
            min={c.xp_depense}
            libelle="XP gagnés"
            pas={10}
            taille="petit"
            onchange={(v) => ajuster('experience', v - c.xp_total)}
          />
        </span>
      </div>
      <span class="pied">
        {c.xp_depense} dépensés
        · <button type="button" class="lien" onclick={() => (depense = !depense)}>
          {depense ? 'Refermer' : 'Dépenser'}
        </button>
      </span>
      <div
        class="barre"
        role="progressbar"
        aria-valuenow={c.xp_disponible}
        aria-valuemin="0"
        aria-valuemax={c.xp_total}
      >
        <i style:width="{part(c.xp_disponible, c.xp_total)}%"></i>
      </div>
    </div>

    <div class="jauge">
      <div class="entete">
        <span class="titre">
          Influence
          {#if c.notoriete}<span class="badge notoriete">{c.notoriete}</span>{/if}
        </span>
        <span class="compte">
          <NombreSaisi
            valeur={c.pi_disponibles}
            min={0}
            max={c.pi_cumules}
            libelle="PI disponibles"
            pas={5}
            taille="grand"
            onchange={(v) => ajuster('influence_disponible', v - c.pi_disponibles)}
          />
          /
          <NombreSaisi
            valeur={c.pi_cumules}
            min={0}
            libelle="PI cumulés"
            pas={5}
            taille="petit"
            onchange={(v) => ajuster('influence', v - c.pi_cumules)}
          />
        </span>
      </div>
      <span class="pied">{c.pi_cumules - c.pi_disponibles} dépensés</span>
      <div
        class="barre"
        role="progressbar"
        aria-valuenow={c.pi_disponibles}
        aria-valuemin="0"
        aria-valuemax={c.pi_cumules}
      >
        <i style:width="{part(c.pi_disponibles, c.pi_cumules)}%"></i>
      </div>
    </div>
  </div>
</div>

<style>
  .cartouche {
    margin-bottom: calc(var(--pas) * 3);
  }
  .cartouche .corps {
    padding: calc(var(--pas) * 3);
    gap: calc(var(--pas) * 3);
  }
  .progression .corps {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(17rem, 1fr));
    gap: calc(var(--pas) * 4);
  }

  .caracs {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(5.5rem, 1fr));
    gap: calc(var(--pas) * 2);
  }
  .chiffre {
    display: grid;
    justify-items: center;
    gap: calc(var(--pas) * 0.5);
    background: var(--surface-2);
    border: 1px solid var(--line);
    border-radius: var(--r);
    padding: calc(var(--pas) * 2.5) calc(var(--pas) * 1.5);
  }
  .etiquette {
    font-size: 0.68rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--ink-soft);
  }
  .chiffre .valeur {
    font-size: 1.6rem;
    font-weight: 600;
    line-height: 1;
  }

  .capacites {
    list-style: none;
    padding: 0;
    margin: 0;
    display: grid;
    gap: calc(var(--pas) * 2);
  }
  .capacites li {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: calc(var(--pas) * 0.5) calc(var(--pas) * 2);
    padding-bottom: calc(var(--pas) * 1.5);
    border-bottom: 1px solid var(--line);
  }
  .capacites li:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
  .capacites strong {
    font-family: 'Cinzel', serif;
    font-weight: normal;
  }
  .resume {
    grid-column: 1 / -1;
    font-size: 0.85rem;
    color: var(--ink-soft);
  }

  .compte {
    display: flex;
    align-items: baseline;
    gap: calc(var(--pas) * 0.5);
    white-space: nowrap;
  }
  .titre .badge {
    font-size: 0.6rem;
    padding: 2px 8px;
    margin-left: var(--pas);
  }
  .barre :global(i) {
    transition: width 120ms ease-out;
  }
  .pied {
    font-size: 0.7rem;
    color: var(--ink-soft);
  }
</style>
